<template>
  <section v-if="isKnown && !hidden" class="support-action" :class="{ expanded: expanded || contact }">
    <template v-if="isTopicChange && expanded">
      <h4>Start a separate question?</h4>
      <p>Your current chat will stay archived, and a clean conversation will open here.</p>
      <p v-if="hasDraft" class="draft-warning">Your current draft will be deleted.</p>
      <a-alert v-if="requestError" :message="requestError" type="error" show-icon />
      <div class="support-action-controls">
        <a-button :disabled="disabled || loading" @click="dismissSuggestion">Keep this chat</a-button>
        <a-button type="primary" :loading="loading" @click="startNewTopic">Start new question</a-button>
      </div>
    </template>

    <template v-else-if="isMessageLimit && confirmDraftRestart">
      <h4>Restart Chat?</h4>
      <p>Your current draft will be deleted.</p>
      <a-alert v-if="requestError" :message="requestError" type="error" show-icon />
      <div class="support-action-controls">
        <a-button :disabled="disabled || loading" @click="confirmDraftRestart = false"> Cancel </a-button>
        <a-button type="primary" :disabled="disabled" :loading="loading" @click="startNewTopic">
          Restart Chat
        </a-button>
      </div>
    </template>

    <template v-else-if="isMessageLimit">
      <div>
        <h4>This conversation has reached its message limit.</h4>
        <p>Start a new question to continue chatting with ePeople Support.</p>
      </div>
      <a-alert v-if="requestError" :message="requestError" type="error" show-icon />
      <a-button type="primary" :disabled="disabled" :loading="loading" @click="requestNewTopic">
        {{ labels.START_NEW_QUESTION }}
      </a-button>
    </template>

    <template v-else-if="contact">
      <div class="contact-heading">
        <a-avatar :src="contact.photo_url" :size="40">{{ contactInitials }}</a-avatar>
        <div>
          <h4>{{ contact.display_name }}</h4>
          <p v-if="contact.role">{{ contact.role }}</p>
        </div>
      </div>
      <div class="contact-options">
        <a v-if="contact.phone" :href="`tel:${contact.phone}`">
          <a-icon icon="carbon:phone" width="18" height="18" />
          <span>{{ contact.phone }}</span>
        </a>
        <a v-if="contact.email" :href="`mailto:${contact.email}`">
          <a-icon icon="carbon:email" width="18" height="18" />
          <span>{{ contact.email }}</span>
        </a>
      </div>
    </template>

    <template v-else>
      <button type="button" class="support-action-trigger" :disabled="disabled || loading" @click="handleAction">
        <span class="support-action-icon">
          <a-icon :icon="icon" width="22" height="22" />
        </span>
        <span class="support-action-copy">
          <strong>{{ label }}</strong>
          <small v-if="description">{{ description }}</small>
          <small v-if="contactSummary">{{ contactSummary }}</small>
        </span>
        <a-icon v-if="!loading" icon="carbon:chevron-right" width="20" height="20" />
        <a-icon v-else icon="carbon:circle-dash" width="20" height="20" class="loading-icon" />
      </button>
      <div v-if="requestError" class="support-action-error">
        <span>{{ requestError }}</span>
        <a-button size="small" @click="handleAction">Retry</a-button>
      </div>
    </template>
  </section>
</template>

<script>
import { Alert, Avatar, Button } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import {
  SUPPORT_ACTION_LABELS,
  SUPPORT_ACTION_REASONS,
  SUPPORT_ACTION_TYPES,
  isKnownSupportAction
} from '@/store/modules/agent/constants'

const { mapActions } = createNamespacedHelpers('agent')

export default {
  components: {
    'a-alert': Alert,
    'a-avatar': Avatar,
    'a-button': Button
  },
  props: {
    action: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasDraft: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expanded: false,
      hidden: false,
      loading: false,
      requestError: '',
      contact: null,
      labels: SUPPORT_ACTION_LABELS,
      confirmDraftRestart: false
    }
  },
  computed: {
    isKnown() {
      return isKnownSupportAction(this.action)
    },
    isTopicChange() {
      return (
        this.action.type === SUPPORT_ACTION_TYPES.START_NEW_TOPIC &&
        this.action.reason === SUPPORT_ACTION_REASONS.TOPIC_CHANGED
      )
    },
    isMessageLimit() {
      return (
        this.action.type === SUPPORT_ACTION_TYPES.START_NEW_TOPIC &&
        this.action.reason === SUPPORT_ACTION_REASONS.MESSAGE_LIMIT_REACHED
      )
    },
    isResponsibleContact() {
      return this.action.type === SUPPORT_ACTION_TYPES.OPEN_RESPONSIBLE_CONTACTS
    },
    label() {
      if (this.isTopicChange) return SUPPORT_ACTION_LABELS.ASK_NEW_QUESTION
      if (this.isResponsibleContact) return SUPPORT_ACTION_LABELS.OPEN_RESPONSIBLE_CONTACTS
      if (this.action.reason === SUPPORT_ACTION_REASONS.UNRESOLVED_TECHNICAL_ISSUE) {
        return SUPPORT_ACTION_LABELS.SEND_ISSUE_TO_SUPPORT
      }
      return SUPPORT_ACTION_LABELS.CONTACT_SUPPORT
    },
    description() {
      if (this.action.reason === SUPPORT_ACTION_REASONS.UNRESOLVED_TECHNICAL_ISSUE) {
        return 'Includes this chat and available error details.'
      }
      return ''
    },
    contactSummary() {
      if (!this.isResponsibleContact) return ''
      return [this.action.data?.display_name, this.action.data?.role].filter(Boolean).join(' · ')
    },
    contactInitials() {
      return (this.contact?.display_name ?? '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase()
    },
    icon() {
      if (this.isTopicChange) return 'carbon:restart'
      if (this.isResponsibleContact) return 'carbon:user-profile'
      return 'carbon:help'
    }
  },
  methods: {
    ...mapActions(['postStartNewTopic', 'dismissTopicSuggestion', 'fetchResponsibleContact']),
    handleAction() {
      if (this.disabled || this.loading) return
      this.requestError = ''
      if (this.isTopicChange) {
        this.expanded = true
        return
      }
      if (this.isResponsibleContact) {
        this.openResponsibleContact()
        return
      }
      this.$emit('open-support-form', this.action.reason)
    },
    requestNewTopic() {
      if (this.disabled || this.loading) return
      if (this.hasDraft) {
        this.confirmDraftRestart = true
        return
      }
      this.startNewTopic()
    },
    async startNewTopic() {
      if (this.disabled || this.loading) return
      this.loading = true
      this.$emit('loading-change', true)
      this.requestError = ''
      try {
        const started = await this.postStartNewTopic()
        if (started) this.$emit('conversation-changed')
      } catch (error) {
        this.requestError = 'A new question could not be started. Please try again.'
      } finally {
        this.loading = false
        this.$emit('loading-change', false)
      }
    },
    async dismissSuggestion() {
      if (this.disabled || this.loading) return
      this.loading = true
      this.$emit('dismissed')
      this.requestError = ''
      try {
        const dismissed = await this.dismissTopicSuggestion()
        if (dismissed) this.$emit('conversation-changed')
      } catch (error) {
        this.requestError = 'This suggestion could not be dismissed. Please try again.'
      } finally {
        this.loading = false
      }
    },
    async openResponsibleContact() {
      if (this.disabled || this.loading) return
      this.loading = true
      this.$emit('loading-change', true)
      this.requestError = ''
      try {
        const contact = await this.fetchResponsibleContact({ contactId: this.action.data?.contact_id })
        if (!contact) {
          this.hidden = true
          return
        }
        this.contact = contact
        this.$emit('conversation-changed')
      } catch (error) {
        this.requestError = 'Contact details could not be loaded.'
      } finally {
        this.loading = false
        this.$emit('loading-change', false)
      }
    }
  }
}
</script>

<style scoped>
.support-action {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  padding: 12px;
  color: var(--text-color);
  background: var(--bg-body);
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius);
}

.support-action.expanded {
  padding: 16px;
  background: var(--bg-component);
}

.support-action h4,
.support-action p {
  margin: 0;
}

.support-action p,
.support-action-copy small {
  color: var(--text-color-alpha8);
}

.support-action .draft-warning {
  color: var(--warning-color, #faad14);
}

.support-action-trigger {
  display: flex;
  width: 100%;
  padding: 0;
  align-items: center;
  gap: 12px;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.support-action-trigger:disabled {
  cursor: wait;
}

.support-action-icon {
  display: flex;
  width: 38px;
  height: 38px;
  flex: 0 0 38px;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  background: var(--bg-row-hover);
  border-radius: 10px;
}

.support-action-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.support-action-copy small {
  line-height: 1.4;
}

.support-action-controls {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.support-action-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--text-color);
}

.contact-heading,
.contact-options a {
  display: flex;
  align-items: center;
  gap: 10px;
}

.contact-options {
  display: grid;
  gap: 8px;
}

.contact-options a {
  padding: 9px 10px;
  color: var(--text-color);
  background: var(--bg-component);
  border-radius: calc(var(--border-radius) / 2);
}

.loading-icon {
  animation: action-spin 1s linear infinite;
}

@keyframes action-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 520px) {
  .support-action-controls {
    flex-direction: column-reverse;
  }

  .support-action-controls .ant-btn {
    width: 100%;
  }
}
</style>
